<template>
  <el-drawer
    v-model="is_open"
    class="ui-side-modal"
    :class="'layer-' + layer"
    direction="rtl"
    :size="layerSize"
    :with-header="false"
    :before-close="handleBeforeClose"
    @open="onDrawerOpen"
    @close="onElClose"
  >
    <Scrollbar class="ui-modal-side--container" id="modal-body" :key="is_open">
      <slot />
    </Scrollbar>
  </el-drawer>
</template>
<script setup>
import { computed, nextTick, ref } from 'vue';
import { ElDrawer } from 'element-plus';
import Scrollbar from '@/components/ui/Scrollbar.vue';

const props = defineProps({
  handle_close: {
    type: Boolean,
    default: false,
  },
  layer: {
    type: [Number, String],
    default: 1,
  },
  // Cover layer size, e.g. '80%', '100%'
  size: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['open', 'close', 'creator-next-page-left', 'creator-next-page-right']);

defineOptions({
  name: 'ui-side-modal',
});

const is_open = ref(false);
const is_opening = ref(false);

const layerSize = computed(() => props.size ?? (props.layer === 1 ? '85%' : props.layer === 3? '100%' : '80%'));

const open = () => {
  if (!is_opening.value) {
    is_opening.value = true;
    setTimeout(() => {
      is_opening.value = false;
    }, 1000);
    is_open.value = true;
    emit('open');
  }
};

const close = () => {
  if (props.handle_close) {
    emit('close');
    return;
  }
  if (!is_opening.value) {
    is_opening.value = true;
    setTimeout(() => {
      is_opening.value = false;
    }, 1000);
    is_open.value = false;
  }
};

const closeAgain = () => {
  if (!is_opening.value) {
    is_opening.value = true;
    setTimeout(() => {
      is_opening.value = false;
    }, 1000);
    is_open.value = false;
  }
};

defineExpose({
  open,
  close,
  closeAgain,
});

const handleBeforeClose = (done) => {
  if (props.handle_close) {
    emit('close');
    // Don't call done() — prevent el-drawer from closing
  } else {
    done();
  }
};

const onDrawerOpen = () => {
  nextTick(() => {
    const b = document.getElementById('modal-body');
    if (b) b.addEventListener('scroll', handleScroll);
  });
};

const onElClose = () => {
  emit('close');
  const b = document.getElementById('modal-body');
  if (b) b.removeEventListener('scroll', handleScroll);
};

const handleScroll = () => {
  const modalBody = document.getElementById('modal-body');
  const modal_body = modalBody ? modalBody.scrollTop : 0;
  const body = modal_body + 700;
  let scroll = 0;
  let type = '';

  const leftContent = document.getElementById('scroll-content-left');
  const rightContent = document.getElementById('scroll-content-right');

  if (leftContent) {
    type = 'left';
    scroll = leftContent.offsetHeight;
  } else if (rightContent) {
    type = 'right';
    scroll = rightContent.offsetHeight;
  }

  if (body > scroll) {
    if (type === 'left') emit('creator-next-page-left');
    else if (type === 'right') emit('creator-next-page-right');
  }
};
</script>
<style lang="scss">
.ui-side-modal {
  min-width: 600px;

  &.layer-2 {
    min-width: 560px;
  }

  .el-drawer__body {
    padding: 0;
    overflow: hidden;
    height: 100%;
  }

  .ui-modal-side--container {
    height: 100%;
    padding: 36px 26px;

    header {
      display: flex;

      .title {
        font-family: var(--font-med);
        font-weight: normal;
        font-size: 24px;
        color: var(--black-90);
        flex: 1;

        span {
          display: flex;
          align-items: center;
        }

        svg {
          margin-left: 11px;
          width: 18px;
          height: 18px;
        }
      }

      .icon-box {
        display: flex;

        .icon {
          display: flex;
          margin-left: 20px;
          align-items: center;
          cursor: pointer;
          color: var(--black-75);

          &.i-1 > svg {
            width: 18px;
            height: 18px;
            margin-left: 9px;
            fill: var(--black-75);
          }

          &.download-btn {
            display: flex;
            align-items: center;
            cursor: not-allowed;

            & > svg {
              width: 18px;
              height: 18px;
              fill: var(--black-75);
              margin-right: 6px;
            }
          }

          &.i-2 > svg {
            width: 18px;
            height: 18px;
            fill: var(--black-75);
          }

          &.i-3 > svg {
            width: 14px;
            height: 14px;
            fill: var(--black-75);
          }

          &:hover {
            color: var(--dark-blue);

            svg {
              fill: var(--dark-blue);
            }
          }
        }
      }
    }
  }
}

// mx-datepicker popup appends to body and needs a z-index above el-drawer
.mx-datepicker-popup {
  z-index: 3000 !important;
}
</style>
